import { React, useState } from 'react';
import {
    Box, Flex, Text, Button, Divider,
    Icon, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
    VStack, SimpleGrid, useColorModeValue,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
} from '@chakra-ui/react';
import {
    ExternalLink, FileText, BadgeCheck, Send,
} from 'lucide-react';
import SimpleSidebar from 'views/global/SimpleSidebar';
import BoxDataTable from 'views/global/BoxDataTable';
import SidebarScroll from 'components/scroll/SidebarScroll';
import { QuickAlert } from 'theme/components/alerts';
import handleSidebar from 'lib/handleSidebar';
import { userController } from 'lib/user';
import BeatLoader from 'react-spinners/BeatLoader';
import { genderLabels, docTypeLabels, labelFor } from 'lib/labels';

// ── module-level sub-components ───────────────────────────────────────────────
function InfoRow({ label, value, labelColor }) {
    if (!value) return null;
    return (
        <Flex align="baseline" gap="8px" py="5px" borderBottom="1px solid" borderColor="gray.100">
            <Text fontSize="12px" fontWeight="600" color={labelColor} minW="140px" textTransform="uppercase" letterSpacing="0.05em">
                {label}
            </Text>
            <Text fontSize="14px" color="gray.800">{value}</Text>
        </Flex>
    );
}

function DocItem({ doc, bg }) {
    return (
        <Flex align="center" gap="10px" p="10px" bg={bg} borderRadius="8px">
            <Icon as={FileText} color="brand.500" boxSize="16px" flexShrink={0} />
            <Text fontSize="14px" flex={1} noOfLines={1}>{doc.original_name}</Text>
            <Button
                as="a" href={doc.drive_url} target="_blank" rel="noopener noreferrer"
                size="xs" variant="ghost" colorScheme="brand" flexShrink={0}
                rightIcon={<ExternalLink />}
            >
                Apri
            </Button>
        </Flex>
    );
}

function DocumentsTab(props) {
    const { id, title } = props;

    const { setAlert, clearAlert, showAlert } = QuickAlert();
    const { getSingleRecord, saveRecord } = userController();

    const [status, setStatus] = useState({
        sidebarForm:  false,
        detailLoad:   false,
        saveNewList:  false,
        approving:    false,
        confirmModal: false,
        reminderLoading: false,
    });

    const [detail, setDetail] = useState(null);

    const mutedColor = useColorModeValue('gray.500', 'whiteAlpha.600');
    const labelColor = useColorModeValue('gray.600', 'whiteAlpha.600');
    const rowBg      = useColorModeValue('gray.50',  'whiteAlpha.50');
    const docRowBg   = useColorModeValue('gray.50',  'whiteAlpha.100');

    // ── open sidebar ──────────────────────────────────────────────────────────
    const openDetail = (row) => {
        setDetail(null);
        clearAlert();
        setStatus(s => ({ ...s, sidebarForm: true, detailLoad: true, confirmModal: false }));
        handleSidebar();
        getSingleRecord(`/user/document/${row.id}/detail`)
            .then(res => {
                setDetail(res.data);
                setStatus(s => ({ ...s, detailLoad: false }));
            })
            .catch(() => {
                setAlert('error', { detail: ['Impossibile caricare i dettagli del documento.'] });
                setStatus(s => ({ ...s, detailLoad: false }));
            });
    };

    const hideSidebar = () => {
        setStatus(s => ({ ...s, sidebarForm: false, confirmModal: false, approving: false }));
        setDetail(null);
        handleSidebar(false);
        clearAlert();
    };

    // ── approve ───────────────────────────────────────────────────────────────
    const handleApprove = () => {
        setStatus(s => ({ ...s, approving: true }));
        saveRecord(`/user/document/${detail.id}/approve`, {})
            .then(res => {
                setDetail(d => ({ ...d, closed: true }));
                setAlert('success', res.data.message);
                setStatus(s => ({
                    ...s,
                    approving:    false,
                    confirmModal: false,
                    saveNewList:  !s.saveNewList,
                }));
                SidebarScroll();
            })
            .catch(err => {
                setAlert('error', err.response.data.errors);
                setStatus(s => ({ ...s, approving: false, confirmModal: false }));
                SidebarScroll();
            });
    };


    // ── send reminder ────────────────────────────────────────────────────────
    const sendReminder = () => {
        setStatus(s => ({ ...s, reminderLoading: true }));
        clearAlert();
        saveRecord(`/user/document/${detail.id}/reminder`, {})
            .then(res => {
                setAlert('success', res.data.message);
                setStatus(s => ({ ...s, reminderLoading: false }));
                SidebarScroll();
            })
            .catch(err => {
                setAlert('error', err.response.data.errors);
                setStatus(s => ({ ...s, reminderLoading: false }));
                SidebarScroll();
            });
    };

    // ── table menu ────────────────────────────────────────────────────────────
    const tableMenuItems = [
        { icon: <ExternalLink />, title: 'Vedi Dettagli', handle: (row) => openDetail(row) },
    ];

    // ── sidebar save prop (Approve button in footer) ──────────────────────────
    const saveProp = (!detail || detail.closed) ? undefined : {
        onClick:      () => setStatus(s => ({ ...s, confirmModal: true })),
        text:         'Invia alla Polizia',
        colorScheme:  'green',
        isLoading:    status.approving,
    };

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <>
            <Box>
                {!status.sidebarForm && showAlert()}

                <BoxDataTable
                    title={title}
                    src="/user/document/list"
                    additionalOptions={id ? { property_id: id } : {}}
                    defaultFilters={{ period: '72' }}
                    search={true}
                    checkbox={false}
                    tableMenu={tableMenuItems}
                    trigger={status.saveNewList}
                    defaultCalander="reservation_check_in"
                    tableContent={(item) => (
                        <Box key={item.id} onClick={() => openDetail(item)} cursor="pointer">
                            <Text className="reservation-item-title" mb="6px">
                                <Box as="span" display="inline-block" w="9px" h="9px" borderRadius="full" me="6px" position="relative" top="0px"
                                    bg={item.status === 'completed' ? 'green.400' : item.status === 'pending' ? 'orange.400' : 'red.400'}
                                />
                                {item.reservation?.name ?? `Prenotazione #${item.reservation_id}`}
                            </Text>
                            <Flex gap="16px" flexWrap="wrap">
                                {item.reservation?.property && (
                                    <Text className="muted-text">{item.reservation.property}</Text>
                                )}
                                {item.reservation?.check_in && (
                                    <Text className="muted-text">
                                        {item.reservation.check_in} → {item.reservation.check_out}
                                    </Text>
                                )}
                                <Text className="muted-text">
                                    {item.guests_completed}/{item.guests_total} ospiti completati
                                </Text>
                            </Flex>
                        </Box>
                    )}
                />

                {/* ── Detail sidebar ── */}
                <SimpleSidebar
                    status={status.sidebarForm}
                    title="Documenti Ospiti"
                    cancel={{ onClick: hideSidebar, text: 'Chiudi' }}
                    viewOnly={!detail || !!detail.closed}
                    save={saveProp}
                    loading={status.detailLoad}
                    leftAction={detail && !detail.closed ? (
                        <Button
                            variant="outline"
                            colorScheme="brand"
                            fontWeight="light"
                            leftIcon={status.reminderLoading ? null : <Send />}
                            _hover={{ bg: 'brand.500', color: 'white' }}
                            transition="background-color 0.2s linear"
                            isDisabled={status.reminderLoading}
                            onClick={sendReminder}
                        >
                            {status.reminderLoading
                                ? <BeatLoader loading={true} size={8} aria-label="Loading Spinner" color="#422CFA" />
                                : 'Invia Promemoria'
                            }
                        </Button>
                    ) : null}
                >
                    {status.sidebarForm && showAlert()}

                    {status.detailLoad && (
                        <Flex justify="center" py="40px">
                            <BeatLoader size={10} color="#422CFA" />
                        </Flex>
                    )}

                    {!status.detailLoad && detail && (
                        <Box>
                            {/* Reservation header */}
                            <Box bg={rowBg} borderRadius="12px" p="16px" mb="20px">
                                <Text fontSize="11px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.08em" mb="10px">
                                    Riepilogo Prenotazione
                                </Text>
                                {detail.reservation?.property && (
                                    <Text fontSize="18px" fontWeight="700" color="gray.800" mb="2px">
                                        {detail.reservation.property}
                                    </Text>
                                )}
                                <Text fontSize="16px" fontWeight="700" color="gray.800" mb="12px">
                                    Prenotazione #{detail.reservation?.id}
                                </Text>
                                <SimpleGrid columns={2} gap="8px">
                                    <Box bg="white" borderRadius="8px" px="12px" py="8px">
                                        <Text fontSize="11px" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="0.05em">Check-in</Text>
                                        <Text fontSize="14px" fontWeight="600">{detail.reservation?.check_in}</Text>
                                    </Box>
                                    <Box bg="white" borderRadius="8px" px="12px" py="8px">
                                        <Text fontSize="11px" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="0.05em">Check-out</Text>
                                        <Text fontSize="14px" fontWeight="600">{detail.reservation?.check_out}</Text>
                                    </Box>
                                </SimpleGrid>
                                <Flex align="center" gap="10px" mt="12px" flexWrap="wrap">
                                    {detail.public_url && (
                                        <Button
                                            as="a" href={detail.public_url} target="_blank" rel="noopener noreferrer"
                                            size="xs" variant="outline" colorScheme="brand"
                                            rightIcon={<ExternalLink />}
                                        >
                                            Link Ospite
                                        </Button>
                                    )}
                                    {detail.pin && (
                                        <Text fontSize="12px" color={mutedColor} fontWeight="600">
                                            PIN: <Text as="span" color="gray.800" fontWeight="700" letterSpacing="0.1em">{detail.pin}</Text>
                                        </Text>
                                    )}
                                </Flex>
                            </Box>

                            {/* Status dot */}
                            <Flex align="center" gap="8px" mb="20px">
                                <Box
                                    w="10px" h="10px" borderRadius="full" flexShrink={0}
                                    bg={detail.closed ? 'green.400' : 'orange.400'}
                                />
                                <Text fontSize="14px" fontWeight="600" color={detail.closed ? 'green.600' : 'orange.500'}>
                                    {detail.closed ? 'Inviato alla Polizia' : 'In attesa di revisione'}
                                </Text>
                            </Flex>

                            <Divider mb="20px" />

                            {/* Guest accordions */}
                            {detail.guests?.length > 0 && (
                                <Accordion allowMultiple defaultIndex={[0]} mb="40px">
                                    {detail.guests.map((g, i) => (
                                        <AccordionItem
                                            key={g.id}
                                            border="none"
                                            borderBottom="1px solid rgba(0,0,0,0.06)"
                                            mb="4px"
                                        >
                                            <AccordionButton px="4px" py="16px" _hover={{ bg: 'transparent' }}>
                                                <Flex flex={1} align="center" gap="10px">
                                                    <Text fontSize="14px" fontWeight="600" textAlign="left">
                                                        {(g.given_name || g.surname)
                                                            ? `${g.given_name ?? ''} ${g.surname ?? ''}`.trim()
                                                            : `Ospite ${i + 1}`}
                                                    </Text>
                                                    <Text fontSize="12px" color={labelColor} fontWeight="400">
                                                        {g.is_main_guest ? 'Principale' : 'Aggiuntivo'}
                                                    </Text>
                                                </Flex>
                                                <AccordionIcon />
                                            </AccordionButton>

                                            <AccordionPanel px="4px" pb="28px" pt="8px">
                                                {/* Personal info */}
                                                <Text fontSize="11px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.07em" mb="6px" mt="8px">
                                                    Dati Personali
                                                </Text>
                                                <VStack spacing={0} align="stretch" mb="14px">
                                                    <InfoRow label="Nome Completo" value={[g.given_name, g.surname].filter(Boolean).join(' ')} labelColor={labelColor} />
                                                    <InfoRow label="Email"        value={g.email} labelColor={labelColor} />
                                                    <InfoRow label="Telefono"     value={g.phone} labelColor={labelColor} />
                                                    <InfoRow label="Sesso"        value={labelFor(genderLabels, g.gender)} labelColor={labelColor} />
                                                    <InfoRow label="Data di Nascita" value={g.date_of_birth} labelColor={labelColor} />
                                                    <InfoRow label="Nazionalità"  value={g.nationality} labelColor={labelColor} />
                                                    <InfoRow label="Stato di Nascita" value={g.country_of_birth} labelColor={labelColor} />
                                                    <InfoRow label="Stato di Residenza" value={g.country_of_residency} labelColor={labelColor} />
                                                </VStack>

                                                {/* Document info */}
                                                {g.is_main_guest && (g.doc_type || g.doc_number) && (
                                                    <>
                                                        <Text fontSize="11px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.07em" mb="6px">
                                                            Documento di Identità
                                                        </Text>
                                                        <VStack spacing={0} align="stretch" mb="14px">
                                                            <InfoRow label="Tipo Documento"  value={labelFor(docTypeLabels, g.doc_type)} labelColor={labelColor} />
                                                            <InfoRow label="Numero Documento" value={g.doc_number} labelColor={labelColor} />
                                                            <InfoRow label="Data di Scadenza" value={g.doc_expiry} labelColor={labelColor} />
                                                            <InfoRow label="Luogo di Rilascio" value={g.doc_town_of_issue} labelColor={labelColor} />
                                                        </VStack>
                                                    </>
                                                )}

                                                {/* Uploaded files */}
                                                {g.documents?.length > 0 && (
                                                    <>
                                                        <Text fontSize="11px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.07em" mb="8px">
                                                            Documenti Caricati
                                                        </Text>
                                                        <VStack spacing="6px" align="stretch">
                                                            {g.documents.map(doc => (
                                                                <DocItem key={doc.id} doc={doc} bg={docRowBg} />
                                                            ))}
                                                        </VStack>
                                                    </>
                                                )}
                                            </AccordionPanel>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            )}
                        </Box>
                    )}
                </SimpleSidebar>

                {/* ── Approve confirmation modal ── */}
                <Modal isOpen={status.confirmModal} onClose={() => setStatus(s => ({ ...s, confirmModal: false }))} isCentered size="sm">
                    <ModalOverlay />
                    <ModalContent borderRadius="16px">
                        <ModalHeader pb="8px">
                            <Flex align="center" gap="10px">
                                <Icon as={BadgeCheck} color="green.500" boxSize="22px" />
                                Invia alla Polizia
                            </Flex>
                        </ModalHeader>
                        <ModalBody pb="8px">
                            <Text fontSize="14px" color="gray.600" lineHeight="1.7">
                                Questi dati saranno inviati alla Polizia di Stato.
                                Dopo l'invio gli ospiti non potranno più apportare modifiche. Vuoi procedere?
                            </Text>
                        </ModalBody>
                        <ModalFooter gap="10px">
                            <Button
                                variant="ghost" colorScheme="gray" size="sm"
                                onClick={() => setStatus(s => ({ ...s, confirmModal: false }))}
                                isDisabled={status.approving}
                            >
                                Annulla
                            </Button>
                            <Button
                                colorScheme="green" size="sm"
                                isLoading={status.approving}
                                onClick={handleApprove}
                            >
                                Sì, invia alla Polizia
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </Box>
        </>
    );
}

export default DocumentsTab;
